import AppDialog from "@/components/AppDialog";
import FormattedNumberInput from "@/components/inputs/FormattedNumberInput";
import LocationInput from "@/components/inputs/LocationInput";
import SiteLayout from "@/components/layouts/SiteLayout";
import MediaUploader, {
  MediaUploaderMedia,
} from "@/components/lists/MediaUploader";
import RequestToPublish from "@/components/lists/RequestToPublish";
import ListingController from "@/server/controllers/ListingController";
import LocationService from "@/server/services/locations/locationService";
import UserService from "@/server/services/user/UserService";
import { useTranslations } from "@/TranslationContext";
import CommonUtils from "@/utils/CommonUtils";
import ListingsHelper from "@/utils/ListingsHelper";
import { PropertyType, UsageType } from "@prisma/client";
import axios from "axios";
import { useEffect, useState } from "react";
import { LuArrowRight } from "react-icons/lu";

export async function getServerSideProps(context) {
  const queryParams = context.query;
  const { req, res } = context;

  const { query } = context;
  const { id } = query;

  const maxMediaFiles = process.env.MAX_MEDIA_FILES;

  let listingUuid = id;

  let regions = await LocationService.getAllRegions();

  let user = await UserService.getLoggedInUser(req, {
    whatsapp_number: true,
    phone: true,
    thumbnail_path: true,
  });

  if (!user.phone) {
    return {
      redirect: {
        destination: `/${CommonUtils.getLocaleFromContext(
          context
        )}agent-admin/account/update?next=/agent-admin/post`,
        permanent: false,
      },
    };
  }

  if (listingUuid) {
    let listing = await ListingController.getOneBySeller(listingUuid, user.id);
    if (!listing) {
      return {
        notFound: true,
      };
    }

    let locationRegion = await LocationService.getLocationRegion(
      listing.locationId
    );

    listing = JSON.parse(JSON.stringify(listing));

    return {
      props: {
        regions,
        listing,
        locationRegion,
        maxMediaFiles,
      },
    };
  } else {
    return {
      props: {
        regions,
        maxMediaFiles,
      },
    };
  }
}

export default function PostListing({
  regions,
  listing,
  locationRegion,
  maxMediaFiles,
}) {
  const minDescriptionCharactersLength = 25;
  const maxDescriptionCharactersLength = 3000;

  const [media, setMedia] = useState<MediaUploaderMedia[]>([]);
  const [listingToEdit, setListingToEdit] = useState(listing);
  const [listingType, setListingType] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [numberOfBedrooms, setNumberOfBedrooms] = useState(null);
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(null);
  const [size, setSize] = useState(null);
  const [sizeType, setSizeType] = useState("sqm");
  const [location, setLocation] = useState("");
  const [currency, setCurrency] = useState("tzs");
  const [showInstallmentAllowedSelector, setShowInstallmentAllowedSelector] =
    useState(false);
  const [installmentAllowed, setInstallmentAllowed] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);
  const [availablePaymentModes, setAvailablePaymentModes] = useState([]);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("");
  const [propertyTypeAndUsageType, setPropertyTypeAndUsageType] = useState<{
    property_type: PropertyType;
    usage_type: UsageType;
  }>(null);
  const [isProject, setIsProject] = useState(false);
  const [isFurnished, setIsFurnished] = useState(null);
  const [isPosting, setIsPosting] = useState(false);
  const [priceLabel, setPriceLabel] = useState(null);
  const [hasMasterBedroom, setHasMasterBedroom] = useState(null);
  const [charactersRemaining, setCharactersRemaining] = useState(
    maxDescriptionCharactersLength
  );

  const [selectedRegion, setSelectedRegion] = useState(null);
  const [showRequestToPublish, setShowRequestToPublish] = useState(false);

  const t = useTranslations();
  const locale = t.locale;
  let _hasError = false;

  const offer_types = ListingsHelper.getPostingOfferTypes(t);
  const paymentModes = ListingsHelper.getPaymentModes(t);
  const property_types = ListingsHelper.getPostingPropertyTypes(t);

  const verifyAndPublish = () => {
    validate();
    if (!_hasError) {
      publish();
    }
  };

  const setPayload = () => {
    let payload = {
      property_type: propertyType,
      usage_type: propertyTypeAndUsageType?.usage_type,
      listing_type: listingType == "sale" ? "sale" : "rent",
      description: description,
      price: parseFloat(price),
      currency: currency,
      locationUuid: location,
      media: media.map((m) => {
        return m.uuid;
      }),
      installment_allowed: installmentAllowed,
      payment_mode: paymentMode,
      size: size ? parseFloat(size) : null,
      unit: sizeType,
      is_project: isProject,
      is_furnished: isFurnished,
      number_of_bedrooms: numberOfBedrooms,
      has_master_bedroom: hasMasterBedroom,
      uuid: listingToEdit?.uuid,
    };
    return payload;
  };

  const publish = async () => {
    let payload = setPayload();
    setIsPosting(true);
    const url = "/api/agent-admin/post/post-listing";

    try {
      const response = await axios.post(url, payload);
      let _listing = response.data.data;
      setListingToEdit(_listing);
      setShowRequestToPublish(true);
    } catch (e) {
      _hasError = true;
      setShowError(_hasError);
      setErrorTitle("Error");
      setErrorMessage("Failed to publish listing, please try again");
    }

    setIsPosting(false);
  };

  const validate = () => {
    _hasError = false;
    setShowError(_hasError);
    setErrorTitle("Validation error");
    if (media.length == 0) {
      _hasError = true;
      setShowError(_hasError);
      setErrorMessage("Please upload at least one photo/video file");
      return;
    }

    let allMediaUploaded = true;
    let oneOfMediaFilesFailed = false;
    media.forEach((m) => {
      if (m.uuid == null) {
        allMediaUploaded = false;
      }
    });
    media.forEach((m) => {
      if (m.failed) {
        oneOfMediaFilesFailed = true;
      }
    });
    if (oneOfMediaFilesFailed) {
      _hasError = true;
      setShowError(_hasError);
      setErrorMessage(
        "One of the media files failed to upload, please try re-uploading it or remove it"
      );
      return;
    }
    if (!allMediaUploaded) {
      _hasError = true;
      setShowError(_hasError);
      setErrorMessage("Please wait for all media to upload");
      return;
    }

    if (listingType == "") {
      _hasError = true;
      setShowError(_hasError);
      setErrorMessage("Please select listing type");
      return;
    }

    if (propertyType == "") {
      _hasError = true;
      setShowError(_hasError);
      setErrorMessage("Please select property type");
      return;
    }

    if (description == "") {
      _hasError = true;
      setShowError(_hasError);
      setErrorMessage("Please enter listing details");
      return;
    }

    if (
      description.length > maxDescriptionCharactersLength ||
      description.length < minDescriptionCharactersLength
    ) {
      _hasError = true;
      setShowError(_hasError);
      setErrorMessage(
        `Description text should be ${minDescriptionCharactersLength} to ${maxDescriptionCharactersLength} characters long. Yours is ${description.length} characters`
      );
      return;
    }

    if (price == "") {
      _hasError = true;
      setShowError(_hasError);
      setErrorMessage("Please enter price");
      return;
    }

    if (location == "") {
      _hasError = true;
      setShowError(_hasError);
      setErrorMessage("Please enter location");
      return;
    }

    if (installmentAllowed == 1) {
      if (paymentMode == null) {
        _hasError = true;
        setShowError(_hasError);
        setErrorMessage("Please select installment type");
        return;
      }
    }

    if (propertyTypeAndUsageType?.property_type == "land") {
      if (size == "") {
        _hasError = true;
        setShowError(_hasError);
        setErrorMessage("Please enter plot size");
        return;
      }
    }

    if (
      propertyTypeAndUsageType?.property_type == "building" &&
      propertyTypeAndUsageType?.usage_type == "residential"
    ) {
      if (numberOfBedrooms == null || numberOfBedrooms == "") {
        _hasError = true;
        setShowError(_hasError);
        setErrorMessage("Please enter number of bedrooms");
        return;
      }
    }

    //setShowError(false);
  };

  useEffect(() => {
    populateFields();
  }, [listingToEdit]);

  useEffect(() => {
    if (propertyType) {
      let propertyTypeAndUsageType =
        ListingsHelper.getPropertyTypeAndUsageTypeFromFromPropertyType(
          propertyType
        );
      setPropertyTypeAndUsageType(propertyTypeAndUsageType);
    }
  }, [propertyType]);

  useEffect(() => {
    if (description) {
      setCharactersRemaining(
        maxDescriptionCharactersLength - description.length
      );
    }
  }, [description]);

  useEffect(() => {
    if (listingType == "sale") {
      setShowInstallmentAllowedSelector(true);
    } else {
      setShowInstallmentAllowedSelector(false);
      setInstallmentAllowed(false);
    }
    _setAvailablePaymentModes();
  }, [listingType]);

  useEffect(() => {
    setPriceLabel(paymentMode);
  }, [paymentMode]);

  const _setAvailablePaymentModes = () => {
    let _paymentModes = paymentModes.filter((mode) => {
      if (
        (propertyType === "land" ||
          propertyType === "residential_plot" ||
          propertyType === "farm" ||
          propertyType === "industrial_plot") &&
        listingType === "sale"
      ) {
        return [
          "total",
          "per_sqm",
          "per_acre",
          "per_quarter",
          "semi-annually",
          "annually",
        ].includes(mode.value);
      } else {
        return ![
          "per_sqm",
          "per_acre",
          "per_quarter",
          "semi-annually",
          "annually",
        ].includes(mode.value);
      }
    });

    setAvailablePaymentModes(_paymentModes);
  };

  const populateFields = () => {
    if (listingToEdit?.uuid) {
      setListingType(listingToEdit.listing_type);
      setPropertyType(
        CommonUtils.getPropertyTypeForSearchAndPostingFromListingData(
          listingToEdit
        )
      );
      setDescription(listingToEdit.description);
      setPrice(listingToEdit.price);
      setLocation(listingToEdit.location.uuid);
      setMedia(
        listingToEdit.media.map((m: any) => {
          let media: MediaUploaderMedia = {
            file: null,
            previewUrl: CommonUtils.getImagePath(m.attachment.path),
            isUploading: false,
            failed: false,
            mediaType: m.attachment.type,
            uuid: m.attachment.uuid,
          };
          return media;
        })
      );
      setInstallmentAllowed(listingToEdit.installment_allowed);
      setPaymentMode(
        paymentModes.find((m) => m.value == listingToEdit.payment_mode)
      );
      setSize(listingToEdit.size);
      setSizeType(listingToEdit.size_unit);
      setIsProject(listingToEdit.is_project);
      setIsFurnished(listingToEdit.is_furnished);
      setNumberOfBedrooms(listingToEdit.number_of_bedrooms);
      setHasMasterBedroom(listingToEdit.has_master_bedroom);
      if (locationRegion?.id) {
        setSelectedRegion(locationRegion.id);
      }
    }
  };

  const goToUsersPosts = () => {
    window.location.href = `/agent-admin/listings`;
  };

  return (
    <SiteLayout
      title={"Post Listing " + CommonUtils.appName}
      description="Post Listing "
    >
      {showError && (
        <AppDialog
          type="error"
          title={errorTitle}
          text={errorMessage}
          onClose={() => {
            setShowError(false);
          }}
        />
      )}
      {showRequestToPublish && (
        <AppDialog
          type="custom"
          title={errorTitle}
          text={errorMessage}
          onClose={() => {
            setShowError(false);
          }}
          children={
            <RequestToPublish
              uuid={listingToEdit?.uuid}
              onClose={() => {
                setShowRequestToPublish(false);
              }}
              title={
                locale == "sw" ? listingToEdit?.title : listingToEdit?.title_en
              }
              price={listingToEdit?.price}
              currency={listingToEdit?.currency}
              onPublish={() => {
                goToUsersPosts();
              }}
            />
          }
        />
      )}
      {isPosting && (
        <AppDialog type="loader" title="Posting" text="Please wait..." />
      )}
      <div className="container mx-auto mb-20">
        <div className="space-y-4 mb-10">
          <h2 className="text-3xl">{t.postListing}</h2>
          <div>
            <label>
              {t.uploadMedia}{" "}
              <span className="text-sm text-gray-500">
                ({t.shouldNotExceed} {maxMediaFiles})
              </span>
            </label>
            <MediaUploader
              mediaList={media}
              maxMediaFiles={maxMediaFiles}
              onMediaChange={(media) => {
                setMedia(media);
              }}
            />
          </div>
          <div>
            <label>{t.offerType}*</label>
            <select
              className="w-full border border-gray-300 rounded-lg p-4"
              onChange={(e) => {
                setListingType(e.target.value);
              }}
              value={listingType || ""}
            >
              {offer_types.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>{t.propertyType}*</label>
            <select
              className="w-full border border-gray-300 rounded-lg p-4"
              onChange={(e) => {
                setPropertyType(e.target.value);
                _setAvailablePaymentModes();
              }}
              value={propertyType || ""}
            >
              {property_types.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>{t.region}*</label>
            <select
              className="w-full border border-gray-300 rounded-lg p-4"
              onChange={(e) => {
                setSelectedRegion(e.target.value);
              }}
              value={selectedRegion || ""}
            >
              <option value={""}>Select</option>
              {regions.map((region: any) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
          </div>

          {selectedRegion && (
            <div className="flex-1">
              <label
                htmlFor="buy-properties"
                className="form-label text-slate-900 font-medium"
              >
                {t.location}*
              </label>
              <div className={"filter-search-form relative mt-2 "}>
                <LocationInput
                  className="w-full border border-gray-300 rounded-lg p-4"
                  value={location}
                  region={selectedRegion}
                  placeholder={
                    "Search location in " +
                    regions.find((r) => r.id == selectedRegion).name
                  }
                  onChange={(v: any) => {
                    setLocation(v.value);
                  }}
                />
              </div>
            </div>
          )}
          {showInstallmentAllowedSelector && (
            <div className="flex-1">
              <div>
                <label>{t.isProject}*</label>
              </div>

              <select
                className="w-full border border-gray-300 rounded-lg p-4"
                onChange={(e) => {
                  setIsProject(e.target.value == "1" ? true : false);
                }}
                value={isProject ? "1" : "0"}
              >
                <option value="0">{t.no}</option>
                <option value="1">{t.yes}</option>
              </select>
            </div>
          )}
          <div>
            <label>{t.description}*</label>
            <textarea
              className="w-full h-40 border border-gray-300 rounded-lg p-4"
              placeholder="Listing details here"
              maxLength={maxDescriptionCharactersLength}
              minLength={minDescriptionCharactersLength}
              onChange={(e) => {
                if (e.target.value.length > maxDescriptionCharactersLength) {
                  setDescription(
                    e.target.value.substring(0, maxDescriptionCharactersLength)
                  );
                } else {
                  setDescription(e.target.value);
                }
              }}
              defaultValue={description}
            ></textarea>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">
                {description.length || 0}/ {maxDescriptionCharactersLength}{" "}
                characters, min {minDescriptionCharactersLength} characters
              </span>
            </div>
          </div>
          {(propertyType == "house" || propertyType == "apartment") && (
            <div className="flex-1">
              <div>
                <label>{t.isFurnished}*</label>
              </div>
              <select
                className="w-full border border-gray-300 rounded-lg p-4"
                onChange={(e) => {
                  setIsFurnished(
                    e.target.value == "1"
                      ? true
                      : e.target.value == "0"
                      ? false
                      : null
                  );
                }}
                value={isFurnished ? "1" : "0"}
              >
                <option value="">{t.select}</option>
                <option value="0">{t.no}</option>
                <option value="1">{t.yes}</option>
              </select>
            </div>
          )}
          <div className="flex space-x-4">
            {showInstallmentAllowedSelector && (
              <div className="flex-1">
                <div>
                  <label>{t.installmentAllowed}*</label>
                </div>

                <select
                  className="w-full border border-gray-300 rounded-lg p-4"
                  onChange={(e) => {
                    setInstallmentAllowed(
                      e.target.value == "1"
                        ? true
                        : e.target.value == "0"
                        ? false
                        : null
                    );
                    _setAvailablePaymentModes();
                  }}
                  value={installmentAllowed ? "1" : "0"}
                >
                  <option value="0">{t.no}</option>
                  <option value="1">{t.yes}</option>
                </select>
              </div>
            )}
          </div>
          <div className="flex-1">
            <div>
              <label>{t.priceToShow}</label>
            </div>
            <select
              className="w-full border border-gray-300 rounded-lg p-4"
              onChange={(e) => {
                setPaymentMode(
                  paymentModes.find((m) => m.value == e.target.value)
                );
              }}
              value={paymentMode?.value || ""}
            >
              {availablePaymentModes.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            {priceLabel?.priceLabel}
            <div className="flex space-x-4">
              <select
                className="w-[100px] border border-gray-300 rounded-lg p-4"
                onChange={(e) => {
                  setCurrency(e.target.value);
                }}
                value={currency || "TZS"}
              >
                <option value="tzs">TZS</option>
                <option value="usd">USD</option>
              </select>
              <FormattedNumberInput
                className="flex-1 border border-gray-300 rounded-lg p-4"
                placeholder="Price"
                onChange={(value) => {
                  setPrice(value);
                }}
                defaultValue={price}
              />
            </div>
          </div>

          {propertyTypeAndUsageType?.property_type == "building" &&
            propertyTypeAndUsageType?.usage_type == "residential" && (
              <>
                <div>
                  <label>{t.numberOfBedrooms}</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    className="w-full border border-gray-300 rounded-lg p-4"
                    placeholder="Number of bedrooms"
                    onChange={(e) => {
                      setNumberOfBedrooms(
                        e.target.value ? parseInt(e.target.value) : null
                      );
                    }}
                    defaultValue={numberOfBedrooms}
                  />
                </div>
                <div>
                  <label>{t.hasMasterBedroom}</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg p-4"
                    onChange={(e) => {
                      setHasMasterBedroom(
                        e.target.value == "1"
                          ? true
                          : e.target.value == "0"
                          ? false
                          : null
                      );
                    }}
                    value={hasMasterBedroom ? "1" : "0"}
                  >
                    <option value="1">{t.yes}</option>
                    <option value="0">{t.no}</option>
                  </select>
                </div>
              </>
            )}

          <div>
            {t.plotSize}
            <div className="flex space-x-4">
              <input
                type="number"
                min={1}
                max={1000000}
                className="w-1/2 border border-gray-300 rounded-lg p-4"
                placeholder="Plot size"
                onChange={(e) => {
                  setSize(e.target.value);
                }}
                defaultValue={size}
              />
              <select
                className="w-1/2 border border-gray-300 rounded-lg p-4"
                onChange={(e) => {
                  setSizeType(e.target.value);
                }}
                value={sizeType || "sqm"}
              >
                <option value="sqm">Square Meters</option>
                <option value="acre">Acres</option>
              </select>
            </div>
          </div>
        </div>

        <div className="">
          <button
            className="bg-blue-500 grid place-content-center  text-white p-4 rounded-lg w-full"
            onClick={() => {
              verifyAndPublish();
            }}
          >
            <div className="flex items-center space-x-4">
              <span>{t.next}</span>
              <LuArrowRight />
            </div>
          </button>
        </div>
      </div>
    </SiteLayout>
  );
}
